import axios from 'axios';
import { withOfferParams } from './offerLinks.mjs';
import { META_PIXEL_ID, initMetaPixel, isLeadLabel, trackMetaEvent } from './metaPixel.mjs';

export { withOfferParams };

const API = process.env.REACT_APP_BACKEND_URL || '';
const VISIT_DAYS = 90;
const inflight = {};
const viewed = {};

function pagePath() {
  const path = window.location.pathname || '/';
  return path.length > 1 ? path.replace(/\/$/, '') : path;
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value) {
  const maxAge = 60 * 60 * 24 * VISIT_DAYS;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function readVisitId(path = pagePath()) {
  const named = readCookie(`ps_offer_visit:${path}`);
  if (named) return named;
  if (path !== '/offer/enterprise') return null;
  const legacy = readCookie('ps_offer_visit');
  if (legacy) return legacy;
  try {
    return window.localStorage.getItem('ps_offer_visit');
  } catch {
    return null;
  }
}

export function storeVisitId(visitId, path = pagePath()) {
  writeCookie(`ps_offer_visit:${path}`, visitId);
  if (path === '/offer/enterprise') {
    writeCookie('ps_offer_visit', visitId);
    try {
      window.localStorage.setItem('ps_offer_visit', visitId);
    } catch {
      /* storage can be blocked */
    }
  }
}

export function browserMatchKeys() {
  return {
    fbp: readCookie('_fbp'),
    fbc: readCookie('_fbc'),
    obref: readCookie('__obref'),
    landing_url: window.location.href.slice(0, 500),
  };
}

export function trackOfferCta(label) {
  if (typeof window === 'undefined') return;
  const path = pagePath();
  const onOffer = path.startsWith('/offer/');
  const visitId = onOffer ? readVisitId(path) : null;
  if (isLeadLabel(label)) trackPixel('Lead', visitId);
  if (onOffer) trackGoogleConversion('lead', visitId).catch(() => {});
}

export function trackPixel(eventName, visitId) {
  if (typeof window === 'undefined') return;
  initMetaPixel(META_PIXEL_ID, window);
  const options = visitId ? { eventID: `${visitId}:${eventName}` } : undefined;
  trackMetaEvent(window, eventName, { content_name: pagePath() }, options);
}

function ensureGtag(adsId) {
  if (!adsId || window.__psGtagAds) return;
  window.__psGtagAds = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  window.gtag('config', adsId);
}

let configPromise;
function landingConfig() {
  if (!configPromise) {
    configPromise = axios.get(`${API}/api/public/landing/config`).then((response) => response.data).catch(() => ({}));
  }
  return configPromise;
}

export async function trackGoogleConversion(kind, visitId, user) {
  const config = await landingConfig();
  const label = kind === 'lead' ? config.google_ads_lead_label : config.google_ads_signup_label;
  if (!config.google_ads_id || !label || !visitId) return;
  ensureGtag(config.google_ads_id);
  if (user?.email && typeof window.gtag === 'function') {
    window.gtag('set', 'user_data', {
      email: user.email,
      phone_number: user.phone || undefined,
    });
  }
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: `${config.google_ads_id}/${label}`,
      transaction_id: `${visitId}:${kind}`,
    });
  }
}

export function offerChannel(searchParams) {
  return searchParams.get('ch') || searchParams.get('channel') || '';
}

export async function ensureOfferVisit(searchParams) {
  const path = pagePath();
  if (!path.startsWith('/offer/')) return null;
  const existing = readVisitId(path);
  if (existing) return existing;
  if (inflight[path]) return inflight[path];
  const clickId = searchParams.get('oppref')
    || searchParams.get('chatgpt_click_id')
    || searchParams.get('oai_click_id')
    || searchParams.get('click_id');
  inflight[path] = axios.post(`${API}/api/public/landing/visits`, {
    page_path: path,
    channel: offerChannel(searchParams),
    fbclid: searchParams.get('fbclid'),
    oppref: searchParams.get('oppref'),
    chatgpt_click_id: clickId,
    gclid: searchParams.get('gclid'),
    gbraid: searchParams.get('gbraid'),
    wbraid: searchParams.get('wbraid'),
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    utm_content: searchParams.get('utm_content'),
    utm_term: searchParams.get('utm_term'),
    landing_url: window.location.href.slice(0, 500),
    referrer: (document.referrer || '').slice(0, 500),
    ...browserMatchKeys(),
  }).then((response) => {
    const visitId = response.data.visit_id;
    storeVisitId(visitId, path);
    return visitId;
  }).catch((error) => {
    delete inflight[path];
    throw error;
  });
  return inflight[path];
}

export async function trackOfferPage(searchParams) {
  const path = pagePath();
  const visitId = await ensureOfferVisit(searchParams);
  if (!visitId || viewed[path]) return visitId;
  viewed[path] = true;
  trackPixel('ViewContent', visitId);
  return visitId;
}
